/**
 * Inversion result helpers shared by InversionPanel, SectionView, the 3D viewer and
 * ThemeMapPanel: depth of investigation (DOI), layer-property theme maps and the
 * time-lapse difference, plus the diverging colour ramp.
 *
 * Mirrors backend/inversion_results.py (the server versions serve stored models:
 * GET /api/inversion/{id}/doi, POST /{id}/theme, POST /timelapse-diff). Models the user
 * imports in the map panel only live in the browser, so the same algorithms run here.
 */

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

export const DEFAULT_DOI_THRESHOLD = 0.1;
const MAX_COLUMNS = 400;

// ── columns ──────────────────────────────────────────────────────────────────

const r3 = (v) => Math.round(v * 1000) / 1000;

/** Column key per cell: "x" for 2D lines, "x|y" for 3D. Wide unstructured meshes are binned along x. */
export function columnKeys(cells) {
  const has3d = cells.length > 0 && cells[0].y !== undefined;
  if (has3d) return cells.map((c) => `${r3(c.x)}|${r3(c.y)}`);
  const xs = cells.map((c) => c.x);
  const uniq = new Set(xs.map(r3));
  if (uniq.size > MAX_COLUMNS) {
    let lo = Infinity; let hi = -Infinity;
    xs.forEach((x) => { if (x < lo) lo = x; if (x > hi) hi = x; });
    const width = (hi - lo) / MAX_COLUMNS || 1;
    return xs.map((x) => String(r3(lo + (Math.min(MAX_COLUMNS - 1, Math.floor((x - lo) / width)) + 0.5) * width)));
  }
  return xs.map((x) => String(r3(x)));
}

/** [top, bottom] depth per cell: mesh cells carry top/bottom, table cells use the next depth level. */
export function cellExtents(cells) {
  const levels = [...new Set(cells.map((c) => c.depth))].sort((a, b) => a - b);
  const next = new Map();
  levels.forEach((d, i) => {
    next.set(d, i + 1 < levels.length ? levels[i + 1] : d + (i > 0 ? d - levels[i - 1] : 1));
  });
  return cells.map((c) => [c.top !== undefined ? c.top : c.depth, c.bottom !== undefined ? c.bottom : next.get(c.depth)]);
}

// ── DOI ──────────────────────────────────────────────────────────────────────

/**
 * Per-column DOI: walking down from the most sensitive cell, the depth where sensitivity
 * normalised by the column maximum first drops below `threshold` (interpolated linearly);
 * columns that never do are reliable to their base. Without a sensitivity column every
 * column gets `fallbackDoi` (the importer's estimate), source 'meta'.
 *
 * @returns {{ threshold, source: 'sensitivity'|'meta'|'none', columns: Map<string, number> }}
 */
export function computeDoiProfile(cells, threshold = DEFAULT_DOI_THRESHOLD, fallbackDoi = null) {
  const columns = new Map();
  if (!cells || !cells.length) return { threshold, source: 'none', columns };
  const hasSens = cells.some((c) => isNum(c.sensitivity));
  const keys = columnKeys(cells);
  const ext = cellExtents(cells);
  const groups = new Map();
  keys.forEach((k, i) => { if (!groups.has(k)) groups.set(k, []); groups.get(k).push(i); });
  groups.forEach((idx, key) => {
    idx.sort((a, b) => cells[a].depth - cells[b].depth);
    const base = Math.max(...idx.map((i) => ext[i][1]));
    let doi = null;
    if (hasSens) {
      const sens = idx.map((i) => (isNum(cells[i].sensitivity) ? Math.max(0, cells[i].sensitivity) : NaN));
      let peak = -1; let max = 0;
      sens.forEach((s, j) => { if (isNum(s) && s > max) { max = s; peak = j; } });
      if (peak >= 0) {
        doi = base;
        for (let j = peak + 1; j < idx.length; j += 1) {
          const n1 = sens[j] / max;
          if (isNum(n1) && n1 < threshold) {
            const n0 = sens[j - 1] / max;
            const d0 = cells[idx[j - 1]].depth; const d1 = cells[idx[j]].depth;
            const frac = !isNum(n0) || n0 <= n1 ? 0 : (n0 - threshold) / (n0 - n1);
            doi = d0 + frac * (d1 - d0);
            break;
          }
        }
      }
    }
    if (doi === null) doi = isNum(fallbackDoi) ? fallbackDoi : null;
    if (doi !== null) columns.set(key, doi);
  });
  return { threshold, source: hasSens ? 'sensitivity' : 'meta', columns };
}

/** DOI of the column holding `cell` (undefined when the profile has none). */
export function doiAt(profile, cell, fallbackDoi) {
  if (!profile || !profile.columns.size) return fallbackDoi;
  const key = cell.y !== undefined ? `${r3(cell.x)}|${r3(cell.y)}` : null;
  if (key !== null) return profile.columns.get(key) ?? fallbackDoi;
  const v = profile.columns.get(String(r3(cell.x)));
  if (v !== undefined) return v;
  // binned / unstructured mesh: nearest column
  let best; let bestD = Infinity;
  profile.columns.forEach((d, k) => { const dx = Math.abs(Number(k) - cell.x); if (dx < bestD) { bestD = dx; best = d; } });
  return best ?? fallbackDoi;
}

/**
 * The DOI-mask predicate: true for a cell that lies below its column's DOI (hidden or greyed
 * when the mask is on). Built once per (cells, threshold), then called per cell.
 * With the mask off, or no usable DOI, nothing is below it.
 */
export function makeDoiMask(cells, { mask = true, threshold = DEFAULT_DOI_THRESHOLD, fallbackDoi = null } = {}) {
  if (!mask) return () => false;
  const profile = computeDoiProfile(cells, threshold, fallbackDoi);
  return (cell) => {
    const d = doiAt(profile, cell, fallbackDoi);
    return isNum(d) && cell.depth > d;
  };
}

/** The DOI as [[x, depth], ...] sorted by x (2D) — what SectionView draws. */
export function doiLine(profile) {
  return [...profile.columns.entries()]
    .filter(([k]) => !k.includes('|'))
    .map(([k, d]) => [Number(k), d])
    .sort((a, b) => a[0] - b[0]);
}

// ── colour ramps ─────────────────────────────────────────────────────────────

export const DIVERGING_COLORS = ['#2166ac', '#67a9cf', '#f7f7f7', '#ef8a62', '#b2182b'];
const DIVERGING_STOPS = DIVERGING_COLORS.map((h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16)));

/** Symmetric limit for a diverging scale: the largest |value| (clipped at `pct` percentile, 0-1). */
export function divergingLimit(values, pct = 1) {
  const abs = values.filter(isNum).map(Math.abs).sort((a, b) => a - b);
  if (!abs.length) return 1;
  const v = abs[Math.min(abs.length - 1, Math.floor(pct * (abs.length - 1)))];
  return v > 0 ? v : 1;
}

/** Blue-white-red colour centred on zero: -absMax is blue, 0 is white, +absMax is red. */
export function divergingColor(value, absMax) {
  const lim = absMax > 0 ? absMax : 1;
  const t = Math.max(0, Math.min(1, 0.5 + value / (2 * lim)));
  const pos = t * (DIVERGING_STOPS.length - 1);
  const lo = Math.floor(pos); const hi = Math.min(DIVERGING_STOPS.length - 1, lo + 1); const fr = pos - lo;
  const rgb = DIVERGING_STOPS[lo].map((c, i) => Math.round(c + (DIVERGING_STOPS[hi][i] - c) * fr));
  return `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
}

/** MapLibre circle paint for a layer whose `normalized` property is 0.5 at zero (diverging). */
export const DIVERGING_PAINT = {
  'circle-radius': 4,
  'circle-color': ['interpolate', ['linear'], ['to-number', ['get', 'normalized'], 0.5],
    0, DIVERGING_COLORS[0], 0.25, DIVERGING_COLORS[1], 0.5, DIVERGING_COLORS[2], 0.75, DIVERGING_COLORS[3], 1, DIVERGING_COLORS[4]],
  'circle-opacity': 0.9,
  'circle-stroke-width': 0,
};

/**
 * Slice/QC GeoJSON for a diverging property: `normalized` is 0.5 at zero (see DIVERGING_PAINT),
 * metadata {min, max, colors, diverging} feeds the map's ColorScaleLegend.
 * @param cells [{ lon, lat, value, depth }]
 */
export function divergingGeoJSON(cells, property = 'resistivity_pct_change') {
  const pts = cells.filter((c) => isNum(c.value) && c.lon !== undefined);
  if (!pts.length) return null;
  const lim = divergingLimit(pts.map((c) => c.value));
  return {
    type: 'FeatureCollection',
    features: pts.map((c) => ({
      type: 'Feature',
      properties: {
        value: Math.round(c.value * 1e4) / 1e4,
        normalized: Math.round(Math.max(0, Math.min(1, 0.5 + c.value / (2 * lim))) * 1e4) / 1e4,
        depth: c.depth,
      },
      geometry: { type: 'Point', coordinates: [c.lon, c.lat] },
    })),
    metadata: { min: -lim, max: lim, property, diverging: true, colors: DIVERGING_COLORS },
  };
}

/** Properties of a time-lapse difference model and whether each is drawn diverging (centred on 0). */
export const DIFF_PROPS = [
  { id: 'resistivity_pct_change', label: '% change', diverging: true, unit: '%' },
  { id: 'resistivity_diff', label: 'Absolute change (Ωm)', diverging: true, unit: 'Ωm' },
  { id: 'log_ratio', label: 'log10 ratio (B/A)', diverging: true, unit: '' },
  { id: 'resistivity_a', label: 'Model A', diverging: false, unit: 'Ωm' },
  { id: 'resistivity_b', label: 'Model B', diverging: false, unit: 'Ωm' },
];

export const isDivergingProp = (prop) => DIFF_PROPS.some((p) => p.id === prop && p.diverging);

/** The three steps of the date slider and the property each shows. */
export const DATE_STEPS = [
  { id: 'a', label: 'Model A', prop: 'resistivity_a' },
  { id: 'diff', label: 'Difference', prop: 'resistivity_pct_change' },
  { id: 'b', label: 'Model B', prop: 'resistivity_b' },
];

// ── theme maps ───────────────────────────────────────────────────────────────

export const THEME_KINDS = [
  { id: 'top_depth', label: 'Top of layer (depth where it crosses a threshold)', unit: 'm' },
  { id: 'thickness', label: 'Thickness between two thresholds', unit: 'm' },
  { id: 'layer_stat', label: 'Mean / min / max between two depths', unit: '' },
  { id: 'interface_depth', label: 'Depth to the strongest interface', unit: 'm' },
];

/** Sensible starting values for a theme given the model. */
export function defaultThemeParams(kind, model) {
  const maxDepth = model?.depthRange?.[1] || 10;
  const res = (model?.cells || []).map((c) => c.resistivity).filter((v) => isNum(v) && v > 0).sort((a, b) => a - b);
  const q = (p) => (res.length ? res[Math.floor(p * (res.length - 1))] : 100);
  const round = (v) => Number(v.toPrecision(3));
  switch (kind) {
    case 'top_depth': return { threshold: round(q(0.5)), direction: 'above' };
    case 'thickness': return { min_value: round(q(0.25)), max_value: round(q(0.75)) };
    case 'layer_stat': return { depth_min: 0, depth_max: round(maxDepth / 2), stat: 'mean' };
    default: return { depth_min: 0, depth_max: round(maxDepth) };
  }
}

/**
 * Validate and normalise theme parameters from form strings.
 * @returns {{ params } | { error: string }}
 */
export function buildThemeParams(kind, raw) {
  const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
  const bad = (v) => v !== null && !Number.isFinite(v);
  const p = { property: raw.property || 'resistivity' };
  if (kind === 'top_depth') {
    const thr = num(raw.threshold);
    if (thr === null || bad(thr)) return { error: 'Enter a threshold value' };
    p.threshold = thr;
    p.direction = raw.direction === 'below' ? 'below' : 'above';
  } else if (kind === 'thickness') {
    const lo = num(raw.min_value); const hi = num(raw.max_value);
    if (bad(lo) || bad(hi)) return { error: 'Thresholds must be numbers' };
    if (lo === null && hi === null) return { error: 'Enter a lower and/or an upper threshold' };
    if (lo !== null && hi !== null && lo > hi) return { error: 'The lower threshold must not exceed the upper one' };
    if (lo !== null) p.min_value = lo;
    if (hi !== null) p.max_value = hi;
  } else if (kind === 'layer_stat') {
    const d0 = num(raw.depth_min); const d1 = num(raw.depth_max);
    if (d0 === null || d1 === null || bad(d0) || bad(d1)) return { error: 'Enter the top and bottom depth' };
    if (!(d0 < d1)) return { error: 'The top depth must be above the bottom depth' };
    p.depth_min = d0; p.depth_max = d1;
    p.stat = ['mean', 'min', 'max'].includes(raw.stat) ? raw.stat : 'mean';
  } else if (kind === 'interface_depth') {
    const d0 = num(raw.depth_min); const d1 = num(raw.depth_max);
    if (bad(d0) || bad(d1)) return { error: 'Depths must be numbers' };
    if (d0 !== null && d1 !== null && !(d0 < d1)) return { error: 'The top depth must be above the bottom depth' };
    if (d0 !== null) p.depth_min = d0;
    if (d1 !== null) p.depth_max = d1;
  } else {
    return { error: `Unknown theme '${kind}'` };
  }
  return { params: p };
}

/** Body for POST /api/inversion/{id}/theme. */
export const themeRequestBody = (kind, params) => ({ theme: kind, ...params });

/** Layer-property map of one model, in the browser: GeoJSON points, one per column. */
export function computeTheme(cells, kind, params) {
  const prop = params.property || 'resistivity';
  const logProp = prop === 'resistivity';
  const usable = cells.filter((c) => isNum(c[prop]) && (!logProp || c[prop] > 0));
  if (!usable.length) throw new Error(`The model has no '${prop}' values`);
  const keys = columnKeys(usable);
  const ext = cellExtents(usable);
  const groups = new Map();
  keys.forEach((k, i) => { if (!groups.has(k)) groups.set(k, []); groups.get(k).push(i); });
  const dLo = params.depth_min ?? null; const dHi = params.depth_max ?? null;
  const results = [];
  groups.forEach((idx, key) => {
    idx.sort((a, b) => usable[a].depth - usable[b].depth);
    const depth = idx.map((i) => usable[i].depth);
    const val = idx.map((i) => usable[i][prop]);
    const top = idx.map((i) => ext[i][0]); const bot = idx.map((i) => ext[i][1]);
    let value = null; const extra = {};
    if (kind === 'top_depth') {
      const keep = depth.map((d) => (dLo === null || d >= dLo) && (dHi === null || d <= dHi));
      const dd = depth.filter((_, j) => keep[j]); const vv = val.filter((_, j) => keep[j]);
      const hit = vv.map((v) => (params.direction === 'below' ? v <= params.threshold : v >= params.threshold));
      const j = hit.indexOf(true);
      if (j === 0) value = dd[0];
      else if (j > 0) {
        const f = vv[j] !== vv[j - 1] ? (params.threshold - vv[j - 1]) / (vv[j] - vv[j - 1]) : 0;
        value = dd[j - 1] + f * (dd[j] - dd[j - 1]);
      }
    } else if (kind === 'thickness') {
      let sum = 0; let any = false;
      val.forEach((v, j) => {
        if ((params.min_value === undefined || v >= params.min_value) && (params.max_value === undefined || v <= params.max_value)) {
          sum += bot[j] - top[j]; any = true;
        }
      });
      if (any) value = sum;
    } else if (kind === 'layer_stat') {
      const sel = val.filter((_, j) => bot[j] > dLo && top[j] < dHi);
      if (sel.length) {
        if (params.stat === 'min') value = Math.min(...sel);
        else if (params.stat === 'max') value = Math.max(...sel);
        else if (logProp) value = 10 ** (sel.reduce((s, v) => s + Math.log10(v), 0) / sel.length);
        else value = sel.reduce((s, v) => s + v, 0) / sel.length;
      }
    } else if (kind === 'interface_depth' && idx.length >= 2) {
      let bestJump = -1; let bestEdge = null;
      for (let j = 0; j < idx.length - 1; j += 1) {
        const edge = (bot[j] + top[j + 1]) / 2;
        if ((dLo !== null && edge < dLo) || (dHi !== null && edge > dHi)) continue;
        const jump = Math.abs((logProp ? Math.log10(val[j + 1]) : val[j + 1]) - (logProp ? Math.log10(val[j]) : val[j]));
        if (jump > bestJump) { bestJump = jump; bestEdge = edge; }
      }
      if (bestEdge !== null) { value = bestEdge; extra.contrast = Math.round(bestJump * 1e4) / 1e4; }
    }
    if (isNum(value)) results.push({ key, value, extra, cell: usable[idx[0]] });
  });
  const vals = results.map((r) => r.value);
  const vmin = vals.length ? Math.min(...vals) : 0; const vmax = vals.length ? Math.max(...vals) : 1;
  const span = vmax - vmin || 1;
  const geo = usable[0].lon !== undefined;
  return {
    type: 'FeatureCollection',
    features: results.map(({ value, extra, cell }) => ({
      type: 'Feature',
      properties: { value: Math.round(value * 1e4) / 1e4, normalized: Math.round(((value - vmin) / span) * 1e4) / 1e4, x: cell.x, ...extra },
      geometry: { type: 'Point', coordinates: geo ? [cell.lon, cell.lat] : [cell.x, cell.y ?? 0] },
    })),
    metadata: { theme: kind, params, property: prop, min: vmin, max: vmax, n_columns: groups.size, n_values: results.length, georeferenced: geo },
  };
}

// ── time-lapse difference ────────────────────────────────────────────────────

const stable = (a, b, rel = 1e-6) => Math.abs(a - b) <= 1e-9 + rel * Math.max(Math.abs(a), Math.abs(b));

/** Null when two model entries can be compared, else the reason (same rules as the server's 409). */
export function modelMismatch(a, b) {
  if ((a.model.geometryKind || 'line') !== (b.model.geometryKind || 'line')) return 'Models have a different geometry (e.g. a 2D line and a 3D grid)';
  const ga = a.georef; const gb = b.georef;
  if (!!ga !== !!gb) return 'Only one of the models is georeferenced';
  if (ga && gb) {
    if (String(ga.crs) !== String(gb.crs)) return `Models use different CRS (${ga.crs} vs ${gb.crs})`;
    const ta = ga.transform || {}; const tb = gb.transform || {};
    const keys = new Set([...Object.keys(ta), ...Object.keys(tb)]);
    for (const k of keys) {
      if (!(k in ta) || !(k in tb) || !stable(ta[k], tb[k])) return 'Models have different georeferences (their survey lines are not the same)';
    }
  }
  return null;
}

function nearestLevel(levels, v) {
  let lo = 0; let hi = levels.length - 1;
  while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (levels[mid] <= v) lo = mid; else hi = mid; }
  return Math.abs(levels[lo] - v) <= Math.abs(levels[hi] - v) ? lo : hi;
}

/**
 * B - A on A's cells, in the browser (table-like meshes: x [, y] and depth on regular levels).
 * B is matched to A by nearest level on every axis; A cells further than 2 spacings from any
 * B cell are dropped. Cells gain resistivity_a / resistivity_b / resistivity (= B) /
 * resistivity_diff / resistivity_pct_change / log_ratio.
 */
export function diffCells(cellsA, cellsB, tolerance = 0.25) {
  const ca = cellsA.filter((c) => c.resistivity > 0);
  const cb = cellsB.filter((c) => c.resistivity > 0);
  if (!ca.length || !cb.length) throw new Error('A model has no positive resistivity values');
  if ((ca[0].y !== undefined) !== (cb[0].y !== undefined)) throw new Error('Models have a different geometry (2D vs 3D)');
  const axes = ca[0].y !== undefined ? ['x', 'y', 'depth'] : ['x', 'depth'];
  const levels = {}; const spacing = {};
  axes.forEach((ax) => {
    levels[ax] = [...new Set(cb.map((c) => r3(c[ax])))].sort((a, b) => a - b);
    const la = [...new Set(ca.map((c) => r3(c[ax])))].sort((a, b) => a - b);
    spacing[ax] = la.length > 1 ? Math.min(...la.slice(1).map((v, i) => v - la[i])) : 1;
  });
  const index = new Map(cb.map((c) => [axes.map((ax) => nearestLevel(levels[ax], r3(c[ax]))).join('|'), c]));
  const out = []; let matched = 0; let dropped = 0;
  ca.forEach((a) => {
    const ids = axes.map((ax) => nearestLevel(levels[ax], a[ax]));
    const b = index.get(ids.join('|'));
    const off = b ? axes.map((ax) => Math.abs(a[ax] - b[ax]) / spacing[ax]) : null;
    if (!b || off.some((o) => o > 2)) { dropped += 1; return; }
    if (off.every((o) => o <= tolerance)) matched += 1;
    const { sensitivity, misfit, chargeability, ...keep } = a; // eslint-disable-line no-unused-vars
    out.push({
      ...keep,
      resistivity: b.resistivity,
      resistivity_a: a.resistivity,
      resistivity_b: b.resistivity,
      resistivity_diff: Math.round((b.resistivity - a.resistivity) * 1e6) / 1e6,
      resistivity_pct_change: Math.round(((b.resistivity - a.resistivity) / a.resistivity) * 1e6) / 1e4,
      log_ratio: Math.round(Math.log10(b.resistivity / a.resistivity) * 1e6) / 1e6,
    });
  });
  if (!out.length) throw new Error('The models do not overlap');
  const pct = out.map((c) => c.resistivity_pct_change);
  return {
    cells: out,
    info: {
      n_cells: out.length, n_a: ca.length, n_b: cb.length, matched, resampled: out.length - matched, dropped, tolerance,
      pct_change_min: Math.min(...pct), pct_change_max: Math.max(...pct), pct_change_mean: pct.reduce((s, v) => s + v, 0) / pct.length,
    },
  };
}

export const DIFF_COLUMNS = ['x', 'depth', 'resistivity', 'resistivity_a', 'resistivity_b', 'resistivity_diff', 'resistivity_pct_change', 'log_ratio'];

/** A model entry (InversionPanel shape) for the difference of two entries; throws on a mismatch. */
export function buildDiffEntry(entryA, entryB, name, tolerance = 0.25) {
  const reason = modelMismatch(entryA, entryB);
  if (reason) throw new Error(reason);
  const { cells, info } = diffCells(entryA.cells, entryB.cells, tolerance);
  const xs = cells.map((c) => c.x); const ds = cells.map((c) => c.depth);
  const model = {
    ...entryA.model,
    name,
    format: 'timelapse-diff',
    columns: DIFF_COLUMNS,
    cells,
    nCells: cells.length,
    xRange: [Math.min(...xs), Math.max(...xs)],
    depthRange: [Math.min(...ds), Math.max(...ds)],
    depths: [...new Set(cells.map((c) => Math.round(c.depth * 10) / 10))].sort((a, b) => a - b),
    rmsPercent: null, iterations: [], iteration: null, nIterations: 0,
    parents: { a: entryA.id, b: entryB.id, a_name: entryA.name, b_name: entryB.name },
    diff: info,
  };
  return {
    id: `diff-${Date.now()}-${entryA.id}-${entryB.id}`,
    name, model, cells, line: entryA.line || null, georef: entryA.georef || null,
    footprint: entryA.footprint || null,
  };
}

/**
 * Stored model record ({ id, name, meta, cells, project_id, dataset_id }) -> a model entry.
 * `line` is the two-point [lon, lat] line of the fence diagram (meta.line); the record's own
 * `line` column is the legacy { start, end } import request and is only used when it is an array.
 */
export function recordToEntry(rec, productToModel) {
  const meta = rec.meta || {};
  const model = productToModel({ ...meta, cells: rec.cells || [] }, '');
  model.name = rec.name;
  if (meta.parents) model.parents = meta.parents;
  if (meta.diff) model.diff = meta.diff;
  const line = Array.isArray(meta.line) ? meta.line : (Array.isArray(rec.line) ? rec.line : null);
  const footprint = meta.footprint ? { type: 'Feature', properties: { kind: 'footprint' }, geometry: meta.footprint } : null;
  return {
    id: `srv-${rec.id}`, backendId: rec.id, name: rec.name, model, cells: model.cells, line,
    georef: meta.georef || null, footprint,
    projectId: rec.project_id || null, datasetId: rec.dataset_id || meta.dataset_id || null,
  };
}
